/**
 * Multiplexes WebSocket subscriptions so multiple hooks/components sharing the
 * same exchange + symbol + stream only open one connection. Reference-counted;
 * when the last listener leaves, disconnect is delayed so quick navigations
 * can reuse the same stream.
 */

import { getLivePricingService } from "@/lib/live-pricing/live-pricing-service";
import { recordLivePriceCache } from "@/lib/live-pricing/live-price-cache";
import type {
  ExchangeCode,
  TokenAvailability,
} from "@/lib/market-data/market-data.types";
import type { PriceUpdate } from "@/lib/live-pricing/live-pricing.types";

/** Delay before tearing down an idle stream after the last subscriber unsubscribes. */
const IDLE_DISCONNECT_MS = 45_000;

export interface LivePriceSubscriber {
  onPrice: (update: PriceUpdate) => void;
  onError?: (err: Error) => void;
}

interface RegistryEntry {
  refCount: number;
  subscribers: Set<LivePriceSubscriber>;
  cleanupAdapter: () => void;
  idleTimer: ReturnType<typeof setTimeout> | null;
}

const registry = new Map<string, RegistryEntry>();

function streamKind(
  futuresOnly: boolean,
  availability: TokenAvailability
): "perp" | "spot" | "none" {
  if (availability.available) return "perp";
  if (!futuresOnly && availability.spotAvailable) return "spot";
  return "none";
}

export function getLivePriceStreamKey(
  exchange: ExchangeCode,
  symbolNorm: string,
  futuresOnly: boolean,
  availability: TokenAvailability
): string {
  const stream = streamKind(futuresOnly, availability);
  return `${exchange}:${symbolNorm}:${futuresOnly ? "f" : "fs"}:${stream}`;
}

export function subscribeSharedLivePrice(
  exchange: ExchangeCode,
  symbolNorm: string,
  availability: TokenAvailability,
  futuresOnly: boolean,
  subscriber: LivePriceSubscriber
): () => void {
  const key = getLivePriceStreamKey(
    exchange,
    symbolNorm,
    futuresOnly,
    availability
  );

  let entry = registry.get(key);
  if (!entry) {
    const subscribers = new Set<LivePriceSubscriber>();
    const pricingService = getLivePricingService();

    const cleanupAdapter = pricingService.subscribe(
      exchange,
      symbolNorm,
      availability,
      (update) => {
        recordLivePriceCache(
          exchange,
          symbolNorm,
          futuresOnly,
          availability,
          update
        );
        const current = registry.get(key);
        if (!current) return;
        current.subscribers.forEach((s) => s.onPrice(update));
      },
      (err) => {
        const current = registry.get(key);
        if (!current) return;
        current.subscribers.forEach((s) => s.onError?.(err));
      },
      { futuresOnly }
    );

    entry = {
      refCount: 0,
      subscribers,
      cleanupAdapter,
      idleTimer: null,
    };
    registry.set(key, entry);
  }

  if (entry.idleTimer) {
    clearTimeout(entry.idleTimer);
    entry.idleTimer = null;
  }

  entry.refCount++;
  entry.subscribers.add(subscriber);

  return () => {
    const current = registry.get(key);
    if (!current) return;

    current.subscribers.delete(subscriber);
    current.refCount--;

    if (current.refCount > 0) return;

    current.idleTimer = setTimeout(() => {
      const again = registry.get(key);
      if (!again || again.refCount > 0) return;
      again.cleanupAdapter();
      registry.delete(key);
    }, IDLE_DISCONNECT_MS);
  };
}
